import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'

export const GROUP_PLAYER_WEIGHT = 5

export const isExpectedCountDisablingEntity = (
  entity: RaidPlanAssignmentEntity,
) =>
  entity.kind === 'collection' ||
  entity.kind === 'everyone' ||
  entity.kind === 'role'

export const getEntitySlotWeight = (entity: RaidPlanAssignmentEntity) => {
  if (isExpectedCountDisablingEntity(entity)) return 0
  if (entity.kind === 'group') return GROUP_PLAYER_WEIGHT
  return 1
}

export const getStoredSlotWeight = (entities: RaidPlanAssignmentEntity[]) => {
  const disabling = entities.some(isExpectedCountDisablingEntity)
  const weight = entities.reduce(
    (sum, entity) => sum + getEntitySlotWeight(entity),
    0,
  )
  return { disabling, weight }
}

export const getAnyPlayerFillerCount = (
  entities: RaidPlanAssignmentEntity[],
  expected?: number,
) => {
  if (typeof expected !== 'number') return 0
  const { disabling, weight } = getStoredSlotWeight(entities)
  if (disabling) return 0
  return Math.max(expected - weight, 0)
}

export const getPlannedDisplayedCount = (
  entities: RaidPlanAssignmentEntity[],
  expected?: number,
) => {
  const { disabling, weight } = getStoredSlotWeight(entities)
  return {
    disabling,
    actual: weight + getAnyPlayerFillerCount(entities, expected),
  }
}

export const appendAnyPlayerFillers = (
  entities: RaidPlanAssignmentEntity[],
  expected: number | undefined,
  boxIndex: number,
): RaidPlanAssignmentEntity[] => {
  const missing = getAnyPlayerFillerCount(entities, expected)
  if (missing === 0) return entities
  return [
    ...entities,
    ...Array.from({ length: missing }, (_, index) => ({
      kind: 'by_role' as const,
      role: 'anyone' as const,
      id: `anyplayer-filler-${boxIndex}-${index}`,
    })),
  ]
}

export const flattenGroupsWithAnyPlayerFillers = (
  groups: RaidPlanAssignmentEntity[][],
  getExpected: (boxIndex: number) => number | undefined,
): RaidPlanAssignmentEntity[] =>
  groups.flatMap((entities, boxIndex) =>
    appendAnyPlayerFillers(entities, getExpected(boxIndex), boxIndex),
  )

export const collectionRoleMatcher = (
  entity: RaidPlanAssignmentEntity,
): ((participantRole: string) => boolean) | null => {
  if (entity.kind === 'everyone') {
    return () => true
  }
  if (entity.kind === 'role') {
    const requested = entity.role === 'heal' ? 'healer' : entity.role
    return (participantRole) =>
      matchesCollectionRole(participantRole, requested)
  }
  if (entity.kind !== 'collection') return null
  if (entity.collection === 'everyone') {
    return () => true
  }
  if (entity.collection === 'tanks') {
    return (participantRole) => matchesCollectionRole(participantRole, 'tank')
  }
  if (entity.collection === 'healers') {
    return (participantRole) => matchesCollectionRole(participantRole, 'healer')
  }
  if (entity.collection === 'dps') {
    return (participantRole) => matchesCollectionRole(participantRole, 'dps')
  }
  if (entity.collection === 'ranged') {
    return (participantRole) => matchesCollectionRole(participantRole, 'ranged')
  }
  if (entity.collection === 'melee') {
    return (participantRole) => matchesCollectionRole(participantRole, 'melee')
  }
  return null
}

export const resolvedEntityHighlightsOwnCharacter = ({
  entity,
  ownCharacterIds,
  selectedParticipants,
  ownUserId,
  membershipByCharacterId,
}: {
  entity: {
    kind: string
    participant?: { characterId: number }
    sourceEntity?: RaidPlanAssignmentEntity
  }
  ownCharacterIds: Set<number>
  selectedParticipants: Array<{ characterId: number; role: string }>
  ownUserId?: number
  membershipByCharacterId?: Record<
    number,
    { characterReference: { userId?: number | null } }
  >
}) => {
  if (entity.kind === 'participant' && entity.participant) {
    if (membershipByCharacterId) {
      return (
        membershipByCharacterId[entity.participant.characterId]
          ?.characterReference.userId === ownUserId
      )
    }
    return ownCharacterIds.has(entity.participant.characterId)
  }
  if (entity.kind !== 'aggregate' || !entity.sourceEntity) return false
  const matcher = collectionRoleMatcher(entity.sourceEntity)
  if (!matcher) return false
  return selectedParticipants.some(
    (participant) =>
      ownCharacterIds.has(participant.characterId) && matcher(participant.role),
  )
}

export const getResolvedAssignmentCountActual = ({
  resolvedEntities,
  selectedParticipants,
  spreadRoleCollections,
}: {
  resolvedEntities: Array<{
    kind: string
    sourceEntity?: RaidPlanAssignmentEntity
    participant?: { participantId: number; role: string }
  }>
  selectedParticipants: Array<{ participantId: number; role: string }>
  spreadRoleCollections: boolean
}) => {
  const resolvedPlayerCount = resolvedEntities.filter(
    (entity) => entity.kind === 'participant',
  ).length
  const groupCount = resolvedEntities.filter(
    (entity) =>
      entity.kind === 'aggregate' && entity.sourceEntity?.kind === 'group',
  ).length
  let actual = resolvedPlayerCount + groupCount * GROUP_PLAYER_WEIGHT
  if (spreadRoleCollections) return actual

  resolvedEntities.forEach((entity) => {
    if (entity.kind !== 'aggregate' || !entity.sourceEntity) return
    const matcher = collectionRoleMatcher(entity.sourceEntity)
    if (!matcher) return
    actual += selectedParticipants.filter((participant) =>
      matcher(participant.role),
    ).length
  })
  return actual
}

const matchesCollectionRole = (participantRole: string, requested: string) => {
  if (requested === 'anyone') return true
  if (requested === 'healer') {
    return participantRole === 'healer' || participantRole === 'heal'
  }
  if (requested === 'dps') {
    return (
      participantRole === 'dps' ||
      participantRole === 'melee' ||
      participantRole === 'ranged'
    )
  }
  return participantRole === requested
}
